import React, { useState, useMemo } from "react";
import {
  useDirectionsQuery,
  useDeleteDirectionMutation,
} from "../../services/basicData/basicData";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import { Trans } from "react-i18next";
import ConfirmModal from "../../CommonElements/ConfirmModal";
import { useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DeleteIcon,
  Edit02Icon,
  AddSquareIcon,
  Navigation01Icon,
} from "@hugeicons/core-free-icons";
import { TableC } from "../../CommonElements/TableC";
import BtnAsLink from "../../Component/BtnAsLink";
import { useClientSearch } from "@/utils/hooks/useClientSearch";
import { useSystem } from "@/utils/hooks/useLocation";

// Empty state component for when no directions are found
const EmptyState = ({ hasSearch }) => {
  const { t } = useTranslation();

  return (
    <div className="empty-state text-center py-5">
      <div className="empty-state-icon mb-3">
        <HugeiconsIcon
          icon={Navigation01Icon}
          size={48}
          className="text-muted"
        />
      </div>
      <h4 className="empty-state-title mb-2">{t("NO_DIRECTIONS_FOUND")}</h4>
      <p className="empty-state-description text-muted mb-4">
        {t("NO_DIRECTIONS_DESCRIPTION")}
      </p>
      {!hasSearch && (
        <Link to="create" className="btn btn-primary">
          <HugeiconsIcon
            icon={Navigation01Icon}
            size={16}
            className="me-2"
          />
          {t("CREATE_FIRST_DIRECTION")}
        </Link>
      )}
    </div>
  );
};

export const Directions = () => {
  const currentPage = 1;
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    directionId: null,
  });
  const { data, isLoading, isFetching } = useDirectionsQuery({
    page: currentPage,
    per_page: 10,
  });
  const directions = data?.data?.data ?? [];
  const { filteredData, ...restClientSearch } = useClientSearch(directions);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {currentSystem} = useSystem();
  const loading = isLoading || isFetching;

  // Delete mutation
  const { mutate: deleteDirection, isPending: isDeleting } =
    useDeleteDirectionMutation();

  // Handle delete modal
  const handleDeleteClick = (directionId) => {
    setDeleteModalState({ isOpen: true, directionId });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, directionId: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.directionId) {
      deleteDirection(deleteModalState.directionId, {
        onSuccess: () => {
          setDeleteModalState({ isOpen: false, directionId: null });
          queryClient.invalidateQueries({ queryKey: ["directions"] });
        },
      });
    }
  };

  // Define columns configuration
  const columns = useMemo(
    () => [
      {
        name: t("NAME_AR"),
        key: "name_ar",
        filterable: true,
      },
      {
        name: t("NAME_EN"),
        key: "name_en",
        filterable: true,
      },
      {
        name: t("CODE"),
        key: "code",
        filterable: true,
      },
      { name: t("ACTIONS"), key: "actions", filterable: false },
    ],
    [t]
  );

  // Define actions function
  const getActions = (rowData) => {
    return [
      {
        key: "edit",
        label: (
          <Link
            to={`/app/${currentSystem}/basic-data/directions/${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            {t("EDIT")}
          </Link>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => handleDeleteClick(rowData.id),
        danger: true,
      },
    ];
  };

  // Create button component
  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link="create"
      disabled={loading}
    >
      {t("CREATE_DIRECTION")}
    </BtnAsLink>
  );

  return (
    <Container fluid>
      <TableC
        data={filteredData}
        columns={columns}
        actions={getActions}
        renderActions={createButton}
        loading={loading}
        {...restClientSearch}
        emptyState={EmptyState}
        minVisibleColumns={2}
        skeletonCount={3}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_DIRECTION"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />
    </Container>
  );
};

